import Role, { MODEL_STR } from "byzantine/src/Role";
import { ContextForm, useFormData, useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { TextInput, Checkbox } from "@narmi/design_system";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Account from "byzantine/src/Account";
import OrganizationUser from "byzantine/src/OrganizationUser";
import { useRolesContext } from "byzantine/src/Role/RolesProvider";
import utils from "../../utils";
import BaseBanner from "../BaseBanner";
import OrgRoleActions from "./OrgRoleActions";
import OrganizationAccessLevelExtraPermissions from "./OrganizationAccessLevelExtraPermissions";
import AccountCheckList from "../AccountCheckList";
import SectionCard from "../SectionCard";
import { validateExtraPermissions } from "./OrganizationEditUser";
import {
  validateAccounts,
  getDefaultLimits,
  buildInitialFormData,
  buildRoleFromFormData,
} from "./RoleFormUtils";

const NON_DUPLICABLE_ROLE_NAMES = new Set([
  OrganizationUser.ROLE.ADMIN,
  OrganizationUser.ROLE.COLLABORATOR,
  OrganizationUser.ROLE.VIEWER,
  OrganizationUser.ROLE.ACCOUNT_HOLDER,
  "No Permissions",
]);

interface OrganizationNewRoleProps {
  accounts?: Account[];
  externalAccounts?: Account[];
  orgId: string;
  features?: Record<string, any>;
  organizationUsers: any[];
  isUsingConsumerBillpayJson?: string;
  isDuplicate?: boolean;
  [key: string]: any;
}

interface OrganizationNewRoleFormProps extends OrganizationNewRoleProps {
  duplicateRole: Role | null;
}

type RedirectState =
  | { redirectTo?: { type: "user"; userUuid: string } }
  | undefined;

function OrganizationNewRoleForm({
  accounts = [],
  externalAccounts = [],
  duplicateRole,
  ...props
}: OrganizationNewRoleFormProps) {
  const { createRole, roles } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isUsingConsumerBillpay = !!JSON.parse(
    props.isUsingConsumerBillpayJson || "false"
  );
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const ContextFormComponent = ContextForm as any;
  const CheckboxComponent = Checkbox as any;
  const AccountCheckListComponent = AccountCheckList as any;
  const BaseBannerComponent = BaseBanner as any;

  const defaultLimits = getDefaultLimits(props.features);

  const initialData = buildInitialFormData({
    role: duplicateRole,
    accounts,
    externalAccounts,
    defaultLimits,
    isDuplicate: !!props.isDuplicate,
  });

  const { onChange, formData } = useFormData(initialData);

  const redirectState = location.state as RedirectState;

  // uncheck "All accounts" box if any internal account is unchecked
  useEffect(() => {
    const update: Record<string, any> = {};
    if (!utils.allAreSelected(formData.accounts)) {
      update[MODEL_STR.ACCOUNT] = false;
    }
    if (Object.keys(update).length > 0) {
      onChange(update);
    }
  }, [formData.accounts]);

  // check all internal account when "All accounts" is selected
  useEffect(() => {
    if (formData[MODEL_STR.ACCOUNT]) {
      const newAccounts = formData.accounts.map((account: any) => {
        const newAccount = new Account(account);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(newAccount as any).selected) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (newAccount as any).selected = !(newAccount as any).selected;
        }
        return newAccount;
      });
      onChange({ accounts: newAccounts });
    }
  }, [formData[MODEL_STR.ACCOUNT]]);

  const onSubmit = async (callback: () => void) => {
    const validationError = validateExtraPermissions(formData);

    if (validationError) {
      setPermissionsError(validationError);
      callback();
      return;
    }

    try {
      setIsCreating(true);
      const { role: generatedRole } = buildRoleFromFormData(formData, roles);

      const newRole = new Role({
        name: formData.role_name,
        description: formData.role_description,
        permissions: generatedRole.permissions,
        limits: generatedRole.limits,
        organization_uuid: props.orgId,
      });

      const createdRole = await createRole(newRole.serialize());
      sendNotification({
        type: "success",
        text: l10n.getString("org-role-create-success"),
      });
      // Wait for toast to render before redirecting
      setTimeout(() => {
        callback();
        if (redirectState?.redirectTo?.type === "user") {
          navigate(`/${redirectState.redirectTo.userUuid}`);
        } else {
          navigate(`/roles/${createdRole.uuid}`);
        }
      }, 1000);
    } catch (apiError) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-create-error"),
      });
      setIsCreating(false);
      callback();
    }
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <BaseBannerComponent
        bannerName={l10n.getString("org-role-new-title")}
        goBack={() => {
          navigate("/roles");
        }}
      />
      <p>{l10n.getString("org-role-new-description")}</p>
      <ContextFormComponent data={formData} onChange={onChange}>
        <div className="section-box">
          <div className="nds-grid nds-span-12">
            <div className="nds-span-6 nds-span-mobile-12 nds-input-style-override">
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                {l10n.getString("org-role-field-name")}
              </label>
              <ContextForm.Field required style={{ marginBottom: "0px" }}>
                <TextInput
                  id="role-name"
                  field="role_name"
                />
              </ContextForm.Field>
            </div>
            <div className="nds-span-6 nds-span-mobile-12 nds-input-style-override">
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                {l10n.getString("org-role-field-description")}
              </label>
              <ContextForm.Field required style={{ marginBottom: "0px" }}>
                <TextInput
                  id="role-description"
                  field="role_description"
                />
              </ContextForm.Field>
            </div>
          </div>
        </div>
        <div className="section-box">
          <span className="section-title section-border nds-span-1">
            {l10n.getString("org-role-section-access")}
          </span>
          <SectionCard isLoading={false} paddingSize={null}>
            <div className="padding--x--l padding--top--l">
              <SectionCard.Title text={l10n.getString("org-role-section-accounts")} />
            </div>
            <div className="permission-check-list">
              <div
                className="inverted-primary-color-checkbox"
                style={{ borderBottom: "1px solid RGBA(var(--border-color))" }}
              >
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <CheckboxComponent
                    field={MODEL_STR.ACCOUNT}
                    label={l10n.getString("org-role-all-accounts-checkbox")}
                  />
                </ContextForm.Field>
              </div>
               <ContextForm.Field
                 required
                 validate={validateAccounts}
                 style={{ marginBottom: "0px" }}
               >
                 <AccountCheckListComponent
                   field="accounts"
                   onChange={onChange}
                   formData={formData}
                 />
               </ContextForm.Field>
            </div>
            <div className="permission-check-list">
              <OrganizationAccessLevelExtraPermissions
                formData={formData}
                onChange={onChange}
                features={props.features}
                isUsingConsumerBillpay={isUsingConsumerBillpay}
                error={permissionsError}
                organizationUsers={props.organizationUsers}
              />
            </div>
          </SectionCard>
        </div>
        <OrgRoleActions
          onSubmit={onSubmit}
          dangerouslyDisableShowLoading
          isDisabled={isCreating}
          onCancel={() => navigate("/roles")}
        />
      </ContextFormComponent>
    </div>
  );
}

function OrganizationNewRoleContent(props: OrganizationNewRoleProps) {
  const { roles, isLoading, error } = useRolesContext();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const duplicateRole = props.isDuplicate
    ? roles.find((role: Role) => role.uuid === roleUuid) ?? null
    : null;

  useEffect(() => {
    if (!props.isDuplicate || isLoading || error) {
      return;
    }

    if (!roleUuid) {
      navigate("/roles", { replace: true });
      return;
    }

    if (!duplicateRole) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate("/roles", { replace: true });
      return;
    }

    if (NON_DUPLICABLE_ROLE_NAMES.has(duplicateRole.name)) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate(`/roles/${duplicateRole.uuid}`, { replace: true });
    }
  }, [duplicateRole, error, isLoading, l10n, navigate, props.isDuplicate, roleUuid, sendNotification]);

  if (props.isDuplicate && error) {
    return <div>{`${l10n.getString("org-role-error-toast")}: ${error.message}`}</div>;
  }

  if (
    props.isDuplicate &&
    (isLoading ||
      !duplicateRole ||
      NON_DUPLICABLE_ROLE_NAMES.has(duplicateRole.name))
  ) {
    return <div>{l10n.getString("org-role-loading")}</div>;
  }

  return <OrganizationNewRoleForm {...props} duplicateRole={duplicateRole} />;
}

export default function OrganizationNewRole(
  props: OrganizationNewRoleProps
): any {
  return <OrganizationNewRoleContent {...props} />;
}
